import React, { useEffect, useState, useMemo } from "react";
import { Card, Button, Spinner, Alert, Badge, Table, Form, Pagination, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { 
  Edit, Users, Calendar, Clock, MapPin, Monitor,
  BookOpen, Target, TrendingUp, UserCog, Phone, Mail,
  ChevronRight, Play, MoreVertical, Eye, ExternalLink, Video, UserCheck,
  Square, ClipboardList, UserPlus, Search, X, ShieldOff, IndianRupee, Star,
  CheckCircle, AlertTriangle
} from "lucide-react";
import { getCurrentUser } from "../../utils/auth";
import { UserAvatar, StatusBadge, EmptyState, PageHeader, Tabs, TabsList, TabsTrigger, TabsContent, BackButton } from "../ui";
import { ProgressBar } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import TransferStudentsModal from "../Common/TransferStudentsModal";
import BatchVideosPage from "../Videos/BatchVideosPage";
import BatchMaterialsPage from "../Materials/BatchMaterialsPage";
import BatchGeneralAssignments from "../Assignments/general/BatchGeneralAssignments";
import "./BatchProfile.scss";
import "../Assignments/general/general-assignments.scss";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const formatTime = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

const calculateProgress = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const now = Date.now();
  
  if (now < start) return 0;
  if (now > end) return 100;
  
  return Math.round(((now - start) / (end - start)) * 100);
};

function InfoRow({ icon: Icon, label, value, href, color = "slate" }) {
  return (
    <div className={`bp-info-row bp-info-row--${color}`}>
      <div className="bp-info-row__icon">
        <Icon size={18} />
      </div>
      <div className="bp-info-row__body">
        <p className="bp-info-row__label">{label}</p>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="bp-info-row__link">
            View Link <ExternalLink size={12} />
          </a>
        ) : (
          <p className="bp-info-row__value">{value || "-"}</p>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subtitle, color = "primary" }) {
  const colorClasses = {
    primary: "bg-primary bg-opacity-10 text-primary",
    emerald: "bg-success bg-opacity-10 text-success",
    amber: "bg-warning bg-opacity-10 text-warning",
    violet: "bg-purple bg-opacity-10 text-purple",
    blue: "bg-info bg-opacity-10 text-info",
    rose: "bg-danger bg-opacity-10 text-danger",
  };
  const cls = colorClasses[color] || colorClasses.primary;

  return (
    <Card className="h-100">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center gap-3">
          <div className={`d-flex align-items-center justify-content-center rounded ${cls}`} style={{ width: 40, height: 40, flexShrink: 0 }}>
            <Icon size={20} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p className="text-uppercase text-muted mb-0" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>{label}</p>
            <p className="mb-0 fw-bold" style={{ fontSize: '1.25rem', lineHeight: 1.2 }}>{value}</p>
            {subtitle && <p className="text-muted mb-0" style={{ fontSize: '0.65rem' }}>{subtitle}</p>}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

/* ─── Attendance Tab ─── */
function AttendanceTab({ batchId }) {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsRes, sessionsRes] = await Promise.all([
          apiClient.get(`/attendance/batch/${batchId}/stats`),
          apiClient.get(`/attendance/batch/${batchId}?limit=10`),
        ]);
        setStats(statsRes.data);
        setSessions(sessionsRes.data.sessions || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchData();
  }, [batchId]);

  if (loading) return <div className="text-center py-4"><Spinner size="sm" animation="border" /></div>;

  if (!stats || stats.totalSessions === 0) {
    return (
      <div className="text-center text-muted py-4" style={{ fontSize: '0.9rem' }}>
        <UserCheck size={32} className="mb-2 opacity-50" />
        <p className="mb-0">No attendance records yet. Attendance is tracked automatically when students join live classes.</p>
      </div>
    );
  }

  const statusLabel = (detail) => {
    if (!detail) return <span className="text-muted">Absent</span>;
    switch (detail.status) {
      case 'present': return <Badge bg="success">Present</Badge>;
      case 'partial': return <Badge bg="warning" text="dark">Partial ({detail.activeMins}m / {detail.sessionMins}m)</Badge>;
      case 'dropped': return <Badge bg="danger">Dropped</Badge>;
      default: return <Badge bg="secondary">Pending</Badge>;
    }
  };

  return (
    <div>
      {/* Per-student stats */}
      <h6 className="fw-bold mb-3" style={{ fontSize: '0.85rem' }}>
        Student Attendance ({stats.totalSessions} session{stats.totalSessions !== 1 ? 's' : ''})
      </h6>
      <Table size="sm" hover responsive style={{ fontSize: '0.82rem' }}>
        <thead>
          <tr>
            <th>Student</th>
            <th style={{ width: 90 }}>Present</th>
            <th style={{ width: 170 }}>Attendance</th>
            <th style={{ width: 130 }}>Last Session</th>
          </tr>
        </thead>
        <tbody>
          {stats.students.map((s) => {
            const lastDetail = s.details && s.details.length > 0 ? s.details[s.details.length - 1] : null;
            return (
              <tr key={s.studentId}>
                <td>{s.name}</td>
                <td>{s.attended} / {s.total}</td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <ProgressBar
                      now={s.percentage}
                      variant={s.percentage >= 75 ? 'success' : s.percentage >= 50 ? 'warning' : 'danger'}
                      style={{ height: 6, flex: 1 }}
                    />
                    <small style={{ minWidth: 32 }}>{s.percentage}%</small>
                  </div>
                </td>
                <td>{statusLabel(lastDetail)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Recent sessions */}
      {sessions.length > 0 && (
        <>
          <h6 className="fw-bold mb-3 mt-4" style={{ fontSize: '0.85rem' }}>Recent Sessions</h6>
          <Table size="sm" hover responsive style={{ fontSize: '0.82rem' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Duration</th>
                <th>Students</th>
                <th>Present</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const dur = s.sessionEnd && s.sessionStart
                  ? Math.round((new Date(s.sessionEnd) - new Date(s.sessionStart)) / 60000) + ' min'
                  : '—';
                const presentCount = (s.participants || []).filter(p => p.attendanceStatus === 'present').length;
                return (
                  <tr key={s._id}>
                    <td>{new Date(s.sessionDate || s.sessionStart).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}</td>
                    <td>{dur}</td>
                    <td>{(s.participants || []).length}</td>
                    <td>{presentCount}</td>
                    <td><Badge bg={s.status === 'live' ? 'success' : 'secondary'}>{s.status}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
}

function StudentsTable({ batchId, courseId }) {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addSelected, setAddSelected] = useState([]);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addToast, setAddToast] = useState({ show: false, message: "", type: "success" });
  const [addSummary, setAddSummary] = useState(null);
  const [reviewMap, setReviewMap] = useState({});
  const pageSize = 8;

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/students/list", {
        params: { batch: batchId, page, pageSize, search, ...(statusFilter && { status: statusFilter }) }
      });
      setStudents(data.students || []);
      setTotal(data.total || 0);
    } catch {
      setStudents([]);
      setTotal(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (batchId) fetchStudents();
  }, [batchId, page, search, statusFilter]);

  // Fetch instructor reviews for this batch (admin only)
  useEffect(() => {
    if (!batchId) return;
    apiClient.get(`/student-reviews/batch/${batchId}`)
      .then(({ data }) => {
        const map = {};
        for (const r of data.reviews || []) {
          map[r.studentId] = r;
        }
        setReviewMap(map);
      })
      .catch(() => {});
  }, [batchId]);

  const totalPages = Math.ceil(total / pageSize);

  const handleTransferSuccess = () => {
    setSelected([]);
    fetchStudents();
  };

  // ── Add Student Modal logic ──
  const batchStudentIds = useMemo(() => new Set(students.map(s => s._id)), [students]);

  const handleAddSearch = async (text) => {
    setAddSearch(text);
    if (!text.trim() || text.trim().length < 2) {
      setAddResults([]);
      return;
    }
    setAddSearching(true);
    try {
      const { data } = await apiClient.get("/students/search", { params: { text: text.trim() } });
      // Filter out students already in this batch
      setAddResults((data.students || []).filter(s => !batchStudentIds.has(s._id)));
    } catch {
      setAddResults([]);
    }
    setAddSearching(false);
  };

  const handleAddSubmit = async () => {
    if (addSelected.length === 0) return;
    setAddSubmitting(true);
    try {
      const { data } = await apiClient.post(`/batches/${batchId}/add-students`, {
        studentIds: addSelected.map(s => s._id),
      });

      if (data.skipped?.length > 0) {
        // Build skipped list enriched with student names from the local selection
        const selectedMap = Object.fromEntries(addSelected.map(s => [s._id, s]));
        const skippedWithNames = data.skipped.map(sk => ({
          ...sk,
          name: selectedMap[sk.studentId]?.name || `Student …${sk.studentId.slice(-6)}`,
        }));
        setAddSummary({ added: data.added, skipped: skippedWithNames });
        setAddSelected([]);
        setAddSearch("");
        setAddResults([]);
        if (data.added > 0) fetchStudents();
      } else {
        // All added, no skips — show quick toast then close
        setAddToast({ show: true, message: data.message || "Students added!", type: "success" });
        setAddSelected([]);
        setAddSearch("");
        setAddResults([]);
        setTimeout(() => {
          setShowAddModal(false);
          setAddToast({ show: false, message: "", type: "success" });
        }, 800);
        fetchStudents();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to add students.";
      setAddToast({ show: true, message: msg, type: "error" });
    }
    setAddSubmitting(false);
  };

  const openAddModal = () => {
    setAddSearch("");
    setAddResults([]);
    setAddSelected([]);
    setAddToast({ show: false, message: "", type: "success" });
    setAddSummary(null);
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Header>
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <Users size={16} className="text-muted" />
              <span className="fw-semibold">Students in this Batch</span>
              <Badge bg="secondary">{total}</Badge>
            </div>
            <div className="d-flex gap-2">
              {selected.length > 0 && (
                <Button variant="warning" size="sm" onClick={() => setShowTransferModal(true)}>
                  Transfer Selected ({selected.length})
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={openAddModal}
              >
                <UserPlus size={14} />
                Add Student
              </Button>
              <Form.Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                size="sm"
                style={{ width: 120 }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="">All</option>
              </Form.Select>
              <Form.Control
                type="text"
                placeholder="Search students..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                style={{ width: 200 }}
                size="sm"
              />
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {students.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No students found
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="mb-0" hover>
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th style={{ width: 40 }}>
                      <Form.Check
                        type="checkbox"
                        checked={selected.length === students.length && students.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelected(students.map(s => s._id));
                          } else {
                            setSelected([]);
                          }
                        }}
                      />
                    </th>
                    <th>Student</th>
                    <th>Contact</th>
                    <th className="text-center">Fee Status</th>
                    <th className="text-center">Instructor Review</th>
                    <th>Joined</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student._id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selected.includes(student._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelected([...selected, student._id]);
                            } else {
                              setSelected(selected.filter(id => id !== student._id));
                            }
                          }}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <UserAvatar name={student.name} size="sm" />
                          <div>
                            <div 
                              className="fw-medium text-primary" 
                              style={{ cursor: 'pointer', fontSize: '0.875rem' }}
                              onClick={() => navigate(`/students/${student._id}`)}
                            >
                              {student.name}
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {student.course?.name || "-"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.75rem' }}>
                          <div className="text-muted">{student.mobile}</div>
                          <div className="text-muted text-truncate" style={{ maxWidth: 180 }}>{student.email}</div>
                        </div>
                      </td>
                      <td className="text-center">
                        {(() => {
                          const ca = student.courseAssignments?.find(a => a.batchId?._id === batchId || a.batchId === batchId);
                          const totalFees = ca ? ca.totalFees : student.totalFees;
                          const pending = ca ? ca.pendingFees : student.pendingFees;
                          if (!totalFees || totalFees <= 0) {
                            return <Badge bg="secondary">Not Set</Badge>;
                          }
                          return pending > 0 ? (
                            <Badge bg="warning" text="dark">₹{pending} Pending</Badge>
                          ) : (
                            <Badge bg="success">Paid</Badge>
                          );
                        })()}
                      </td>
                      <td className="text-center">
                        {(() => {
                          const review = reviewMap[student._id];
                          if (!review) return <span className="text-muted" style={{ fontSize: '0.75rem' }}>—</span>;
                          const avg = ((review.performance + review.participation) / 2).toFixed(1);
                          return (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  <div style={{ textAlign: 'left', fontSize: '0.75rem' }}>
                                    <div>Performance: {review.performance}/5</div>
                                    <div>Participation: {review.participation}/5</div>
                                    {review.instructorId?.name && <div>By: {review.instructorId.name}</div>}
                                    {review.remark && <div className="mt-1 fst-italic">"{review.remark}"</div>}
                                  </div>
                                </Tooltip>
                              }
                            >
                              <span className="d-inline-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
                                <Star size={13} fill="#f59e0b" stroke="#f59e0b" />
                                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{avg}</span>
                              </span>
                            </OverlayTrigger>
                          );
                        })()}
                      </td>
                      <td style={{ fontSize: '0.875rem' }} className="text-muted">
                        {formatDate(student.joinedDate)}
                      </td>
                      <td>
                        <Button 
                          variant="light" 
                          size="sm"
                          onClick={() => navigate(`/students/${student._id}`)}
                        >
                          <ChevronRight size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        
        {totalPages > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
            </span>
            <Pagination className="mb-0">
              <Pagination.Prev 
                onClick={() => setPage(p => p - 1)} 
                disabled={page === 1} 
              />
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={page === pageNum}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}
              <Pagination.Next 
                onClick={() => setPage(p => p + 1)} 
                disabled={page === totalPages} 
              />
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      <TransferStudentsModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        selectedIds={selected}
        onTransfer={handleTransferSuccess}
        mode="students"
      />

      {/* Add Existing Student Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>
            <UserPlus size={18} className="me-2" />
            Add Students to Batch
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addToast.show && (
            <Alert variant={addToast.type === "error" ? "danger" : "success"} dismissible onClose={() => setAddToast(t => ({ ...t, show: false }))}>
              {addToast.message}
            </Alert>
          )}

          {addSummary ? (
            /* ── Result summary after partial/full skip ── */
            <div className="add-result-summary">
              {addSummary.added > 0 && (
                <Alert variant="success" className="d-flex align-items-center gap-2 mb-3">
                  <CheckCircle size={16} />
                  <span>
                    <strong>{addSummary.added}</strong> student{addSummary.added !== 1 ? 's' : ''} added to the batch successfully.
                  </span>
                </Alert>
              )}
              {addSummary.skipped.length > 0 && (
                <>
                  <Alert variant="warning" className="d-flex align-items-center gap-2 mb-2">
                    <AlertTriangle size={16} />
                    <span>
                      <strong>{addSummary.skipped.length}</strong> student{addSummary.skipped.length !== 1 ? 's' : ''} could not be added:
                    </span>
                  </Alert>
                  <Table size="sm" bordered className="mb-0" style={{ fontSize: '0.84rem' }}>
                    <thead className="table-light">
                      <tr>
                        <th>Student</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {addSummary.skipped.map((sk, i) => (
                        <tr key={i}>
                          <td>{sk.name}</td>
                          <td className="text-muted">
                            {sk.reason === 'Already enrolled in this course'
                              ? 'Already enrolled in this course via another batch'
                              : sk.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Search input */}
              <div className="position-relative mb-3">
                <Search size={16} className="position-absolute text-muted" style={{ top: 10, left: 12 }} />
                <Form.Control
                  type="text"
                  placeholder="Search students by name..."
                  value={addSearch}
                  onChange={(e) => handleAddSearch(e.target.value)}
                  style={{ paddingLeft: 36 }}
                />
              </div>

              {/* Selected students chips */}
              {addSelected.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {addSelected.map(s => (
                    <Badge key={s._id} bg="primary" className="d-flex align-items-center gap-1 py-2 px-3" style={{ fontSize: '0.8rem' }}>
                      {s.name}
                      <X size={14} style={{ cursor: 'pointer' }} onClick={() => setAddSelected(prev => prev.filter(p => p._id !== s._id))} />
                    </Badge>
                  ))}
                </div>
              )}

              {/* Search results */}
              {addSearching && (
                <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>
              )}
              {!addSearching && addSearch.trim().length >= 2 && addResults.length === 0 && (
                <div className="text-center text-muted py-3" style={{ fontSize: '0.875rem' }}>
                  No students found matching "{addSearch}"
                </div>
              )}
              {addResults.length > 0 && (
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  <Table size="sm" hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th>
                        <th>Name</th>
                        <th>Mobile</th>
                        <th>Current Course</th>
                      </tr>
                    </thead>
                    <tbody>
                      {addResults
                        .filter(s => !addSelected.some(sel => sel._id === s._id))
                        .map(s => (
                        <tr key={s._id} style={{ cursor: 'pointer' }} onClick={() => setAddSelected(prev => [...prev, s])}>
                          <td>
                            <Form.Check type="checkbox" checked={false} readOnly />
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <UserAvatar name={s.name} size="sm" />
                              <span>{s.name}</span>
                            </div>
                          </td>
                          <td className="text-muted">{s.mobile || "-"}</td>
                          <td className="text-muted">{s.course?.name || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {addSummary ? (
            <Button variant="primary" onClick={() => setShowAddModal(false)}>Done</Button>
          ) : (
            <>
              <Button variant="light" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button
                variant="primary"
                disabled={addSelected.length === 0 || addSubmitting}
                onClick={handleAddSubmit}
              >
                {addSubmitting ? (
                  <><Spinner size="sm" animation="border" className="me-1" /> Adding...</>
                ) : (
                  <>Add {addSelected.length > 0 ? `${addSelected.length} Student${addSelected.length > 1 ? 's' : ''}` : 'Students'}</>
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

const BatchProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [startingBatch, setStartingBatch] = useState(false);
  const [endingBatch, setEndingBatch] = useState(false);
  const [showEndBatchModal, setShowEndBatchModal] = useState(false);
  const [endBatchExpiryDate, setEndBatchExpiryDate] = useState("");
  const [showSetExpiryModal, setShowSetExpiryModal] = useState(false);
  const [setExpiryDate, setSetExpiryDate] = useState("");
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [creatingZoom, setCreatingZoom] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [assignmentCount, setAssignmentCount] = useState(0);
  const [batchStats, setBatchStats] = useState(null);
  const [zoomMode, setZoomMode] = useState(null);

  const user = getCurrentUser();
  const isAdminOrSuper = user?.usertype === "superadmin" || user?.usertype === "admin";

  const fetchBatch = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await apiClient.get(`/batches/${id}`);
      setBatch(data.batch);
    } catch (e) {
      setErr("Failed to load batch details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatch();
    // Fetch zoom mode to conditionally hide platform-hosted messaging
    apiClient.get("/institute/zoom-settings")
      .then(({ data }) => setZoomMode(data.zoomMode || null))
      .catch(() => {});
    // Fetch actual assignment count for this batch
    apiClient.get("/general-assignments/list", { params: { batchId: id } })
      .then(({ data }) => setAssignmentCount(data.assignments?.length || 0))
      .catch(() => {});
    // Fetch batch stats (students, fees, attendance)
    apiClient.get(`/batches/${id}/stats`)
      .then(({ data }) => setBatchStats(data))
      .catch(() => {});
  }, [id]);

  const handleCreateZoomMeeting = async () => {
    setCreatingZoom(true);
    try {
      const { data } = await apiClient.post(`/batches/${id}/create-zoom-meeting`);
      setToast({ show: true, message: data.message || "Zoom meeting created!", type: "success" });
      await fetchBatch();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to create Zoom meeting.";
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setCreatingZoom(false);
    }
  };

  const handleStartBatch = async () => {
    setStartingBatch(true);
    try {
      const { data } = await apiClient.post(`/syllabus/batch/${id}/start-batch`);
      setToast({ show: true, message: data.message || "Batch started successfully!", type: "success" });
      await fetchBatch();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to start batch.";
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setStartingBatch(false);
    }
  };

  const handleEndBatch = async () => {
    setShowEndBatchModal(false);
    setEndingBatch(true);
    try {
      const { data } = await apiClient.post(`/batches/${id}/end-batch`);
      // If an expiry date was set during the end-batch flow, apply it
      if (endBatchExpiryDate) {
        await apiClient.put(`/batches/${id}`, { expiryDate: endBatchExpiryDate });
      }
      setToast({ show: true, message: data.message || "Batch completed successfully!", type: "success" });
      setEndBatchExpiryDate("");
      await fetchBatch();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to end batch.";
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setEndingBatch(false);
    }
  };

  const handleSetExpiry = async () => {
    setSavingExpiry(true);
    try {
      await apiClient.put(`/batches/${id}`, {
        expiryDate: setExpiryDate || null,
      });
      setToast({ show: true, message: setExpiryDate ? "Content expiry date set!" : "Content expiry removed!", type: "success" });
      setShowSetExpiryModal(false);
      await fetchBatch();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update expiry date.";
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setSavingExpiry(false);
    }
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="secondary" />
      </div>
    );

  if (err)
    return (
      <Alert variant="danger" className="my-5 text-center">
        {err}
      </Alert>
    );

  if (!batch)
    return <div className="text-muted text-center my-5">Batch not found.</div>;

  const progress = calculateProgress(batch.startDate, batch.endDate);

  return (
    <div className="batch-profile-modern">
      {/* Top Bar with Back Button */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <BackButton onClick={() => navigate(-1)} />
        <div className="flex-grow-1">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <h2 className="mb-0 fw-bold">{batch.name}</h2>
                <StatusBadge status={batch.status} />
                {batch.status === "Completed" && batch.expiryDate && new Date(batch.expiryDate) < new Date() && (
                  <span className="badge bg-danger bg-opacity-10 text-danger">Content Expired</span>
                )}
                {batch.status === "Completed" && batch.expiryDate && new Date(batch.expiryDate) >= new Date() && (
                  <span className="badge bg-warning bg-opacity-10 text-warning">
                    Expires {new Date(batch.expiryDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                )}
              </div>
              <p className="text-muted mb-0">{batch.course?.name || "-"}</p>
            </div>
            <Button 
              variant="dark" 
              size="sm"
              onClick={() => navigate(`/batches/edit/${id}`)}
              className="d-flex align-items-center gap-1"
            >
              <Edit size={14} />
              Edit Batch
            </Button>
          </div>
        </div>
      </div>

      {/* Batch Details */}
      <div className="row g-4 mb-4">
        <div className="col-lg-8">
          <Card className="h-100">
            <Card.Body className="p-4">
              <div className="row g-4">
                <div className="col-md-4">
                  <InfoRow icon={BookOpen} label="Course" value={batch.course?.name} color="blue" />
                </div>
                <div className="col-md-4">
                  <InfoRow icon={UserCog} label="Instructor" value={batch.instructor?.name} color="violet" />
                </div>
                <div className="col-md-4">
                  <InfoRow icon={Calendar} label="Start Date" value={formatDate(batch.startDate)} color="emerald" />
                </div>
                <div className="col-md-4">
                  <InfoRow icon={Calendar} label="End Date" value={formatDate(batch.endDate)} color="rose" />
                </div>
                {batch.expiryDate && (
                  <div className="col-md-4">
                    <InfoRow icon={Calendar} label="Content Expiry" value={formatDate(batch.expiryDate)} color="amber" />
                  </div>
                )}
                <div className="col-md-4">
                  <InfoRow icon={Clock} label="Class Timing" color="cyan" value={
                    batch.startTime && batch.endTime 
                      ? `${formatTime(batch.startTime)} - ${formatTime(batch.endTime)}` 
                      : batch.startTime ? formatTime(batch.startTime) : "-"
                  } />
                </div>
                <div className="col-md-4">
                  <InfoRow icon={Calendar} label="Schedule" value={batch.batchType === "WEEKEND" ? "Saturday-Sunday" : "Monday-Friday"} color="indigo" />
                </div>
                <div className="col-md-4">
                  <InfoRow icon={Monitor} label="Mode" value={batch.mode} color="teal" />
                </div>
                <div className="col-md-4">
                  <InfoRow icon={MapPin} label="Room" value={batch.room} color="orange" />
                </div>
                {batch.meetingLink && (
                  <div className="col-md-4">
                    <InfoRow icon={ExternalLink} label="Meeting Link" href={batch.meetingLink} color="blue" />
                  </div>
                )}
                {batch.zoomMeetingId && (
                  <div className="col-md-4">
                    <InfoRow icon={Video} label="Zoom Meeting ID" value={batch.zoomMeetingId} color="violet" />
                  </div>
                )}
                {batch.zoomPasscode && (
                  <div className="col-md-4">
                    <InfoRow icon={Video} label="Zoom Passcode" value={batch.zoomPasscode} color="rose" />
                  </div>
                )}
              </div>

              {/* Create Zoom Meeting button for admin/superadmin when no zoom meeting exists */}
              {isAdminOrSuper && (batch.mode === 'Online' || batch.mode === 'Both') && !batch.zoomMeetingId && zoomMode !== 'platformHosted' && (
                <div className="mt-3 p-3 rounded-3" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Video size={18} className="text-warning" />
                    <span className="fw-semibold" style={{ fontSize: '0.875rem' }}>No Zoom meeting linked</span>
                  </div>
                  <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                    This batch doesn't have a Zoom meeting yet. Create one to enable online classes.
                  </p>
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={handleCreateZoomMeeting}
                    disabled={creatingZoom}
                  >
                    {creatingZoom ? (
                      <><Spinner size="sm" animation="border" className="me-1" /> Creating...</>
                    ) : (
                      <><Video size={14} className="me-1" /> Create Zoom Meeting</>
                    )}
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="col-lg-4">
          <Card className="h-100">
            <Card.Body className="p-4 d-flex flex-column gap-4">
              <div>
                <div className="d-flex justify-content-between mb-1">
                  <p className="text-uppercase text-muted mb-0" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                    Batch Progress
                  </p>
                  <span className="fw-semibold" style={{ fontSize: '0.875rem' }}>{progress}%</span>
                </div>
                <ProgressBar value={progress} className="mb-1" />
                <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                  {formatDate(batch.startDate)} to {formatDate(batch.endDate)}
                </p>
              </div>

              <div className="border-top pt-3">
                <div className="d-flex justify-content-between mb-1">
                  <p className="text-uppercase text-muted mb-0" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                    Seat Capacity
                  </p>
                  <span className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                    {batchStats?.activeStudentCount ?? batch.studentCount ?? batch.strength ?? 0}/{batch.effectiveMaxStudents || batch.maxStudents || '∞'}
                  </span>
                </div>
                <ProgressBar value={batch.effectiveMaxStudents || batch.maxStudents ? ((( batchStats?.activeStudentCount ?? batch.studentCount ?? batch.strength ?? 0) / (batch.effectiveMaxStudents || batch.maxStudents)) * 100) : 0} />
              </div>

              {isAdminOrSuper && batch.status !== "Ongoing" && batch.status !== "Completed" && (
                <div className="border-top pt-3">
                  <Button
                    variant="primary"
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={handleStartBatch}
                    disabled={startingBatch}
                  >
                    {startingBatch ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <Play size={16} />
                        Start Batch
                      </>
                    )}
                  </Button>
                </div>
              )}

              {isAdminOrSuper && batch.status === "Ongoing" && (
                <div className="border-top pt-3">
                  <Button
                    variant="outline-danger"
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => setShowEndBatchModal(true)}
                    disabled={endingBatch}
                  >
                    {endingBatch ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <Square size={16} />
                        End Batch
                      </>
                    )}
                  </Button>
                </div>
              )}

              {isAdminOrSuper && batch.status === "Completed" && (
                <div className="border-top pt-3">
                  <Button
                    variant="outline-secondary"
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => {
                      setSetExpiryDate(batch.expiryDate ? new Date(batch.expiryDate).toISOString().substring(0, 10) : "");
                      setShowSetExpiryModal(true);
                    }}
                  >
                    <ShieldOff size={16} />
                    {batch.expiryDate ? "Update Content Expiry" : "Set Content Expiry"}
                  </Button>
                </div>
              )}

              <div className="border-top pt-3">
                <p className="text-uppercase text-muted mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                  Instructor
                </p>
                <div className="d-flex align-items-center gap-3">
                  <UserAvatar name={batch.instructor?.name || "N/A"} size="sm" />
                  <div style={{ minWidth: 0 }}>
                    <p className="mb-0 fw-medium" style={{ fontSize: '0.875rem' }}>
                      {batch.instructor?.name || "N/A"}
                    </p>
                    {batch.instructor?.mobile && (
                      <div className="d-flex align-items-center gap-1 text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                        <Phone size={12} />
                        <span>{batch.instructor.mobile}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg">
          <KpiCard 
            icon={Users} 
            label="Active Students" 
            value={batchStats?.activeStudentCount ?? batch.studentCount ?? batch.strength ?? 0} 
            subtitle={batch.effectiveMaxStudents || batch.maxStudents ? `of ${batch.effectiveMaxStudents || batch.maxStudents} seats` : 'Unlimited seats'}
            color="primary" 
          />
        </div>
        <div className="col-6 col-lg">
          <KpiCard 
            icon={TrendingUp} 
            label="Attendance" 
            value={batchStats ? `${batchStats.avgAttendance}%` : '-'} 
            subtitle={batchStats?.totalSessions ? `${batchStats.totalSessions} session${batchStats.totalSessions !== 1 ? 's' : ''}` : 'No sessions'}
            color="emerald" 
          />
        </div>
        <div className="col-6 col-lg">
          <KpiCard 
            icon={IndianRupee} 
            label="Fees Pending" 
            value={batchStats ? `₹${batchStats.pendingFees.toLocaleString('en-IN')}` : '-'} 
            subtitle={batchStats?.totalFees ? `of ₹${batchStats.totalFees.toLocaleString('en-IN')} total` : 'No fees set'}
            color="rose" 
          />
        </div>
        <div className="col-6 col-lg">
          <KpiCard 
            icon={BookOpen} 
            label="Completion" 
            value={`${progress}%`} 
            subtitle="Batch progress" 
            color="blue" 
          />
        </div>
        <div className="col-6 col-lg">
          <KpiCard 
            icon={Target} 
            label="Assignments" 
            value={assignmentCount} 
            subtitle="Total tasks" 
            color="violet" 
          />
        </div>
      </div>

      {/* Tabs: Details | Students | Videos | Materials */}
      <Tabs defaultValue="students" className="mb-4">
        <TabsList>
          <TabsTrigger value="students">
            <Users size={14} className="me-1" /> Students
          </TabsTrigger>
          <TabsTrigger value="videos">
            <Video size={14} className="me-1" /> Videos
          </TabsTrigger>
          <TabsTrigger value="materials">
            <BookOpen size={14} className="me-1" /> Materials
          </TabsTrigger>
          <TabsTrigger value="attendance">
            <UserCheck size={14} className="me-1" /> Attendance
          </TabsTrigger>
          <TabsTrigger value="assignments">
            <ClipboardList size={14} className="me-1" /> Assignments
          </TabsTrigger>
        </TabsList>

        <TabsContent value="students">
          <StudentsTable batchId={id} courseId={batch?.course?._id || batch?.course} />
        </TabsContent>

        <TabsContent value="videos">
          <BatchVideosPage batchId={id} role={user?.usertype || "admin"} currentUserId={user?._id || user?.id} />
        </TabsContent>

        <TabsContent value="materials">
          <BatchMaterialsPage batchId={id} courseId={batch?.course?._id || batch?.course} role={user?.usertype || "admin"} currentUserId={user?._id || user?.id} />
        </TabsContent>

        <TabsContent value="attendance">
          <AttendanceTab batchId={id} />
        </TabsContent>

        <TabsContent value="assignments">
          <BatchGeneralAssignments batchId={id} />
        </TabsContent>
      </Tabs>

      {/* End Batch Confirmation Modal */}
      <Modal show={showEndBatchModal} onHide={() => setShowEndBatchModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>End Batch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Are you sure you want to mark <strong>"{batch?.name}"</strong> as completed?
          </p>
          <ul className="text-muted" style={{ fontSize: '0.875rem' }}>
            <li>The batch status will change to <Badge bg="secondary">Completed</Badge></li>
            <li>Any live meeting will be ended automatically</li>
            <li>All students in this batch will be notified</li>
            <li>The batch will no longer appear in the calendar or dashboard</li>
            <li>A plan slot will be freed up for a new batch</li>
          </ul>
          <div className="mt-3 p-3 rounded" style={{ background: 'var(--bg-secondary, #f8f9fa)' }}>
            <label className="d-block mb-1 fw-medium" style={{ fontSize: '0.875rem' }}>
              Content Expiry Date <span className="text-muted fw-normal">(optional)</span>
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={endBatchExpiryDate}
              onChange={(e) => setEndBatchExpiryDate(e.target.value)}
              min={new Date().toISOString().substring(0, 10)}
            />
            <small className="text-muted d-block mt-1">
              Students will retain access to content until this date. Leave empty for no restriction.
            </small>
          </div>
          <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.8rem' }}>
            This action cannot be undone from here. You would need to manually edit the batch to change its status back.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowEndBatchModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleEndBatch}>
            <Square size={14} className="me-1" />
            End Batch
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Set Expiry Modal (for already completed batches) */}
      <Modal show={showSetExpiryModal} onHide={() => setShowSetExpiryModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>Set Content Expiry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Set or update the content expiry date for <strong>"{batch?.name}"</strong>.
            Students will lose access to course content after this date.
          </p>
          <div>
            <label className="d-block mb-1 fw-medium" style={{ fontSize: '0.875rem' }}>
              Expiry Date
            </label>
            <input
              type="date"
              className="form-control"
              value={setExpiryDate}
              onChange={(e) => setSetExpiryDate(e.target.value)}
              min={batch?.endDate ? new Date(batch.endDate).toISOString().substring(0, 10) : undefined}
            />
            <small className="text-muted d-block mt-1">
              Must be after the batch end date. Clear to remove expiry restriction.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowSetExpiryModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSetExpiry} disabled={savingExpiry}>
            {savingExpiry ? "Saving..." : "Save Expiry"}
          </Button>
        </Modal.Footer>
      </Modal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default BatchProfile;
